import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Link,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core";
import { get } from "lodash";
import { Link as RouterLink, Redirect } from "react-router-dom";
import { useHistory } from "react-router-dom";

import { Button, FormInput, FormRow, Image, WarningMessage } from "components";
import { breakpoints } from "containers/Responsive";
import { AppContext } from "contexts";
import { getQueryParams } from "lib/helpers";
import * as routes from "routes";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { sellerLogin } from "services/users";
import {
  ACCOUNT_DEACTIVATED,
  INVALID_CREDENTIALS,
} from "services/users/constants";
import { UserLoginErrorMessage } from "services/users/types";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    logo: {
      maxWidth: 200,
      [`@media (max-width: ${breakpoints.desktop - 1}px)`]: {
        maxWidth: 150,
      },
    },
  }),
);

const ERROR_MESSAGE_TO_USER_MESSAGE: Record<UserLoginErrorMessage, string> = {
  [ACCOUNT_DEACTIVATED]:
    "This account is deactivated. If you think this is a mistake, please contact us.",
  [INVALID_CREDENTIALS]:
    "Invalid credentials. Please check your email address and password.",
};

const SellerLogin: React.FC = () => {
  const classes = useStyles();
  const [nextPage, setNextPage] = useState<string>("");
  const [passwordResetLink, setPasswordResetLink] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");

  const appContext = useContext(AppContext);
  const history = useHistory();

  useEffect(() => {
    const next = getQueryParams().next;
    const url = new URL(window.location.toLocaleString());
    const prefixUrl: string = url.origin.replace("worth", "auctions");

    setNextPage(next || null);

    setPasswordResetLink(`${prefixUrl}/accounts/password/reset/`);

    return;
  }, []);

  useEffect(() => {
    const next = getQueryParams().next;

    if (appContext.user && next) {
      history.push(next || routes.sellerDashboard());
    }
  }, [appContext.user, history]);

  const handleLogin = async () => {
    const userLogin = { email, password };
    try {
      const user = await sellerLogin(userLogin);
      appContext.setUser(user);
    } catch (e) {
      setError(
        get(
          ERROR_MESSAGE_TO_USER_MESSAGE,
          (e as Error).message,
          DEFAULT_ERROR_MESSAGE,
        ),
      );
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleLogin();
  };

  if (!!appContext.user) {
    return <Redirect to={nextPage || routes.sellerDashboard()} />;
  }

  return (
    <Box my={2} textAlign="center" width="100%">
      <Container maxWidth="sm">
        <Box mt={3.625} mb={5.625}>
          <Image
            name="orderbook/royalty_exchange_logo.svg"
            className={classes.logo}
          />
        </Box>
        <Card>
          <CardContent>
            <Box my={4}>
              <Typography variant="h6" component="div">
                <Box fontWeight={theme.typography.fontWeightMedium}>
                  Sign in to Your Account
                </Box>
              </Typography>
            </Box>
            {!!error && (
              <Box mb={4} mx={4}>
                <WarningMessage title="Sign In Failed" description={error} />
              </Box>
            )}
            <Box m={2}>
              <form onSubmit={handleSubmit}>
                <FormRow>
                  <FormInput
                    required
                    hideValidationIcon
                    title="Email address"
                    id="email"
                    type="email"
                    onChange={setEmail}
                    value={email}
                    validator={() => true}
                    autoComplete="email"
                    placeholder="john@email.com"
                    autoFocus
                  />
                </FormRow>
                <FormRow>
                  <FormInput
                    required
                    hideValidationIcon
                    title="Password"
                    id="password"
                    type="password"
                    onChange={setPassword}
                    value={password}
                    validator={() => true}
                    maxLength={120}
                    autoComplete="current-password"
                    placeholder="••••••••"
                  />
                </FormRow>
                <Box mb={4}>
                  <Button
                    id="seller-login-button"
                    customColor="green"
                    variant="contained"
                    type="submit"
                    fullWidth
                  >
                    Login
                  </Button>
                </Box>
                <Box my={2}>
                  <Typography
                    component="div"
                    variant="body2"
                    color="textSecondary"
                  >
                    {"Don't have an account? "}
                    <Link
                      variant="body2"
                      color="textSecondary"
                      to="/seller/create-account/"
                      component={RouterLink}
                      underline="always"
                    >
                      Create one!
                    </Link>
                  </Typography>
                </Box>
                <Box my={2}>
                  <Link
                    id="forgot-your-password"
                    href={passwordResetLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    color="textSecondary"
                    underline="always"
                  >
                    Forgot your password?
                  </Link>
                </Box>
              </form>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default SellerLogin;
